/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { createReducer, on } from '@ngrx/store';
import {
    ContractMinDaysStatusActions,
    MarginCalculatorApiActions,
    MarginCalculatorComputationActions,
    MarginCalculatorPageActions,
    PayBillingActions,
    AdditionalCostsActions,
    ReimbursementsActions,
    UpdateMarginActions,
    ReviewAndSendActions,
    PipelineMarginActions,
    DuplicateMarginActions,
    MarginCalculatorUserInterfaceActions,
    ValidateFormActions,
    LinkToOfferActions
} from '../actions';
import * as marginCalculatorActions from '../actions/margin-calculator.actions';
import { MarginCalculatorStateHelpers } from '../state/margin-calculator-state-helpers';
import { MarginCalculatorState } from '../state/margin-calculator.state';
import { MarginState } from '../state/margin-state';
import { RelocationType } from '../../enum/margin-calculator-reimbursement-type.enum';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { BaseBillingRate } from '../../models/base-billing-rate';
import { HousingActions } from '../actions/housing.actions';
import { HousingStateHelpers } from '../state/housing-state-helpers';
import { CERT_OTHER_ID, HOUSEWARE_OTHER_ID } from '../../models';
import { FORM_NAMES } from '../../services/margin-calculator-form.service';
import { InsuranceActions } from '../actions/insurance.actions';
import { ApiActionStatus } from '../../enum';
import { StipendState } from '../state/stipend-state';
import { MarginTypes } from '../../ngrx-components/margin-button-pane/margin-calculator-button-pane.component';
import { MarginType } from '../../enum/margin-type.enum';
import { HousingDefaultState, HousingState } from '../state/housing.state';
import { ControlDefaults } from '../../enum/control-defaults.enum';
import { FormulaHelperService } from '../../services/formula-helper.service';
import Big from 'big.js';
import { ReimbursementsState } from '../state/reimbursements.state';

dayjs.extend(localizedFormat);

export const initialReimbursementsState = {
    certificationTextOther: undefined,
    certificationValueOther: undefined,
    carRentalOneTime: undefined,
    fingerPrintingOneTime: undefined,
    parkingOneTime: undefined,
    parkingMonthly: undefined,
    rentalCar: 0,
    scrubsOneTime: undefined,
    taxableModulesOneTime: undefined,
    hasOneTimeCustomReimbursement: undefined,
    oneTimeCustomReimbursementAmount: undefined,
    oneTimeCustomReimbursementTitle: undefined,
    hasMonthlyCustomReimbursement: undefined,
    monthlyCustomReimbursementAmount: undefined,
    monthlyCustomReimbursementTitle: undefined,
    facilityPaidBonus: undefined,
    ayaPaidBonus: undefined,
    discretionaryBonus: undefined,
    referralBonus: undefined,
    payToBonus: undefined,
    forReferringBonus: undefined,
    stateLicensures: undefined,
    educationReimbursements: undefined,
    selectedPickleAmount: undefined,
    selectedPickleTitle: undefined,
    pickledAmount: undefined,
    reimbursementTotal: undefined,
    estimatedWeeklyCheckPretax: undefined
} satisfies ReimbursementsState;

export const initialMarginState = {
    id: 0,
    fileName: '',
    isDeleted: false,
    dateDeleted: undefined,
    pendingOfficialOffer: false,
    isCompBonus: false,
    isExtension: false,
    isVacation: false,
    isPendingFacilityApproval: false,
    altShift: 0,
    hours: 12,
    hoursAlt: 12,
    shifts: 3,
    shiftsAlt: 3,
    shiftTime: 3,
    shiftTimeAlt: 3,
    startDate: dayjs().toDate().toLocaleDateString(),
    endDate: dayjs().add(89, 'day').toDate().toLocaleDateString(),
    vacationDates: '',
    weeks: 13,
    lastWeeksLength: 13,
    bookingWeeks: 0,
    altBookingWeeks: 0,
    contractDays: 90,
    lastDaysLength: 90,
    shiftH: 19,
    shiftToH: 7,
    shiftM: 0,
    shiftToM: 0,
    shiftAltH: 19,
    shiftAltToH: 7,
    shiftAltM: 0,
    shiftAltToM: 0,
    isOfferMargin: false,
    offerId: null,
    isBillOverTime: false,
    overtimeHours: 0,
    billDailyOtAfter: 0,
    billDorW: ControlDefaults.EmptySelect,
    billDorWM: ControlDefaults.EmptySelect,
    andOr: ControlDefaults.EmptySelect,
    orientationHours: 0,
    orientationFirstHours: 0,
    orientationAmount: 0,
    orientationNextValue: 0,
    orientationNextAmount: 0,
    weeklyHours: 36,
    marginPayRate: 20,
    relocationType: RelocationType.evenSplit,
    relocationFrom: 0,
    relocationTo: 0,
    relocation: 0,
    licenseReimbursement: 0,
    bonus: 0,
    housingDeposit: 0,
    taxRate: 0,
    discountPercent: 0,
    onCallPayRate: 0,
    onCallBillRate: 0,
    callbackPayRate: 0,
    callbackBillRate: 0,
    holidayBillRate: 0,
    holidayPayRate: 0,
    baseBillingRate: { value: 0, resetValidation: false } as BaseBillingRate,
    baseBillingRateAlt: { value: 0, resetValidation: false } as BaseBillingRate,
    marginType: MarginTypes.NewMarginCalculator,
    marginTypeId: MarginType.Travel,
    age: 0,
    medicalCoverage: '',
    medicalMonth: 0,
    dentalMonth: 0,
    certReimb: 0,
    shipping: 0,
    giftCard: undefined,
    oTMult: 0,
    extDiscountType: false,
    nIH: 0,
    finger: 0,
    hWares: 0,
    reimOTSum: 0,
    reimMSum: 0,
    previousProfessionId: 0,
    housing: {
        isAyaHousing: false,
        housingType: HousingStateHelpers.getHousingType(1),
        continuedHousing: false,
        extendedStayHotel: 0,
        monthlyHousing: 0,
        moveInDate: undefined,
        moveOutDate: undefined,
        isFamilyIncluded: false,
        familyRelationship: '',
        familyName: '',
        familyAge: undefined,
        familyNotes: '',
        isPetIncluded: false,
        petType: 0,
        petWeight: undefined,
        petAge: undefined,
        petBreed: '',
        bedrooms: 1,
        moveOutExpenses: HousingStateHelpers.getMoveOutExpenses(1),
        furnitureDelivery: HousingStateHelpers.getFurnitureDelivery(1),
        furniture: HousingStateHelpers.getFurniture(1),
        utilities: HousingStateHelpers.getUtilities(1),
        housewares: [],
        defaults: {
            monthlyHousing: 0
        } satisfies HousingDefaultState
    } satisfies HousingState,
    reimbursements: initialReimbursementsState,
    stipends: {
        mealStipend: 0,
        maxMeals: 0,
        housingStipend: 0,
        maxHousing: 0,
        stipendsPerContract: 0,
        weeklyStipends: 0,
        maxStipends: 0,
        mealStipendPerShift: 0,
        housingStipendPerShift: 0,
        mealStipendPerDay: 0,
        housingStipendPerDay: undefined
    } satisfies StipendState
} as MarginState;

export const initialState: MarginCalculatorState = {
    marginFormReveal: FORM_NAMES.facility_billing_details,
    version: undefined,
    margin: initialMarginState,
    loadedMargin: undefined,
    job: undefined,
    cliniciansByName: undefined,
    cliniciansByEmail: undefined,
    loadFacilityStatus: ApiActionStatus.None,
    loadClinicianStatus: ApiActionStatus.None,
    facilities: undefined,
    isFacilityInvalid: false,
    isClinicianInvalid: false,
    specialties: undefined,
    billingRates: undefined,
    housingInfo: undefined,
    insuranceInfo: undefined,
    extendedInsuranceInfo: undefined,
    reimbursements: undefined,
    gsaRates: undefined,
    calculatedPremiumBillRate: undefined,
    housewares: undefined,
    contractMinDays: {
        loaded: false,
        loading: false,
        status: undefined
    },
    blendedPayRate: 0,
    insuranceEffectiveDates: {
        newEffectiveDate: undefined,
        oldEffectiveDate: undefined,
        releaseNewEffectiveDate: undefined
    },
    errorMessage: undefined,
    showShiftAmt: false,
    originalBillingRate: undefined,
    originalBillingRateAlt: 0,
    reviewAndSendEnabled: true,
    continueWithoutLinking: false,
    minPremiumPayRate: undefined,
    shiftBonus: null,
    changeOfContractMarginId: 0,
    marginsDifferences: null,
    saveMarginAction: {
        actionType: null,
        status: ApiActionStatus.None,
        error: undefined
    },
    contractRequestId: null,
    minimumWageDetails: null,
    userInterfaceState: {
        specialtyControl: {
            isInitialSpecialtyChanged: false
        },
        facilityControl: {
            isInitialFacilityChanged: false
        },
        clinicianControl: {
            isInitialClinicianChanged: false
        },
        isMarginCreatedFromJob: false
    },
    featureFlags: {},
    orientationAllHoursBillable: false,
    apiCallResponses: undefined,
    isBusy: false,
    facilityContractRules: null,
    moneyMovementValidations: [],
    offerAyaPaidBonus: undefined,
    insuranceTotalCostFixEffectiveDate: undefined,
    linkedToJob: false
};

export const marginCalculatorReducer = createReducer(
    initialState,

    on(
        MarginCalculatorPageActions.setFeatureFlag,
        (state, { featureName, isEnabled }): MarginCalculatorState => ({
            ...state,
            featureFlags: {
                ...state.featureFlags,
                [featureName]: isEnabled
            }
        })
    ),

    on(
        MarginCalculatorApiActions.userCredentialsLoadedSuccess,
        (state, { userCredentials }): MarginCalculatorState => ({
            ...state,
            userCredentials
        })
    ),

    on(
        ContractMinDaysStatusActions.loadStatus,
        (state): MarginCalculatorState => ({
            ...state,
            contractMinDays: {
                ...state.contractMinDays,
                loading: true,
                loaded: false
            }
        })
    ),

    on(
        ContractMinDaysStatusActions.loadStatusSuccess,
        (state, action): MarginCalculatorState => ({
            ...state,
            contractMinDays: {
                ...state.contractMinDays,
                loading: false,
                loaded: true,
                status: action.status
            }
        })
    ),

    on(
        ContractMinDaysStatusActions.loadStatusFail,
        (state): MarginCalculatorState => ({
            ...state,
            contractMinDays: {
                ...state.contractMinDays,
                loading: false,
                loaded: false
            }
        })
    ),

    on(
        ContractMinDaysStatusActions.clearStatus,
        (state): MarginCalculatorState => ({
            ...state,
            contractMinDays: {
                ...state.contractMinDays,
                loading: false,
                loaded: false,
                status: null
            }
        })
    ),

    on(
        marginCalculatorActions.loadBillingRates,
        (state, action): MarginCalculatorState => ({
            ...state,
            billingRates: action.billingRates
        })
    ),

    on(
        marginCalculatorActions.loadHousingInfo,
        (state, action): MarginCalculatorState => ({
            ...state,
            housingInfo: action
        })
    ),

    on(
        marginCalculatorActions.loadInsuranceInfo,
        (state, action): MarginCalculatorState => ({
            ...state,
            insuranceInfo: action
        })
    ),

    on(
        marginCalculatorActions.loadExtendedInsuranceInfo,
        (state, action): MarginCalculatorState => ({
            ...state,
            extendedInsuranceInfo: action
        })
    ),

    on(
        marginCalculatorActions.loadReimbursements,
        (state, action): MarginCalculatorState => ({
            ...state,
            reimbursements: action
        })
    ),

    on(
        marginCalculatorActions.loadGSARates,
        (state, action): MarginCalculatorState => ({
            ...state,
            gsaRates: action
        })
    ),

    on(
        MarginCalculatorApiActions.premiumBillingRateLoadedSuccess,
        (state, action): MarginCalculatorState => ({
            ...state,
            apiCallResponses: {
                ...state.apiCallResponses,
                getPremiumBillRateApiResponse: {
                    premiumBillRate: action.premiumBillRate
                }
            }
        })
    ),

    on(
        MarginCalculatorComputationActions.premiumBillingRateComputedSuccess,
        (state, action): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                premiumBillRate: action.premiumBillRate
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setPremiumBillRate,
        (state, action): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                premiumBillRate: action.premiumBillRate
            }
        })
    ),

    on(
        marginCalculatorActions.loadHousewares,
        (state, action): MarginCalculatorState => ({
            ...state,
            housewares: action.housewares
        })
    ),

    on(
        MarginCalculatorApiActions.jobLoadedFail,
        MarginCalculatorApiActions.facilityLoadedFail,
        MarginCalculatorApiActions.facilitiesLoadedFail,
        MarginCalculatorApiActions.clinicianLoadedFail,
        MarginCalculatorApiActions.clinicianNamesLoadedFail,
        MarginCalculatorApiActions.clinicianEmailsLoadedFail,
        MarginCalculatorApiActions.getContractDatesLoadedFail,
        MarginCalculatorApiActions.getContractEndDateLoadedFail,
        MarginCalculatorApiActions.billingRatesLoadedFail,
        MarginCalculatorApiActions.specialtyLoadedFail,
        MarginCalculatorApiActions.insuranceInfoLoadedFail,
        MarginCalculatorApiActions.targetMarginLoadedFail,
        MarginCalculatorApiActions.targetMarginWithExpertiseLoadedFail,
        MarginCalculatorApiActions.targetMarginWithProfessionLoadedFail,
        MarginCalculatorApiActions.premiumBillingRateLoadedFail,
        MarginCalculatorApiActions.professionWithSpecialtiesLoadedFail,
        MarginCalculatorApiActions.offerAyaPaidBonusLoadedFail,
        (state, { message }): MarginCalculatorState => ({
            ...state,
            errorMessage: message
        })
    ),

    on(
        MarginCalculatorApiActions.marginLoadedFail,
        (state, { message }): MarginCalculatorState => ({
            ...state,
            margin: initialMarginState,
            errorMessage: message
        })
    ),

    on(
        MarginCalculatorApiActions.marginLoadedSuccess,
        (state, { margin }): MarginCalculatorState => ({
            ...state,
            margin: MarginCalculatorStateHelpers.convertMarginToState(margin),
            loadedMargin: MarginCalculatorStateHelpers.convertMarginToState(margin),
            minPremiumPayRate: FormulaHelperService.calculateMinPremiumPayRate(margin?.marginPayRate ?? 0)
        })
    ),

    on(
        MarginCalculatorApiActions.jobLoadedSuccess,
        (state, { job }): MarginCalculatorState => ({
            ...state,
            job,
            margin: {
                ...state.margin,
                ...MarginCalculatorStateHelpers.convertJobToState(job)
            },
            loadedMargin: {
                ...state.loadedMargin,
                ...MarginCalculatorStateHelpers.convertJobToState(job)
            },
            userInterfaceState: {
                ...state.userInterfaceState,
                isMarginCreatedFromJob: true
            }
        })
    ),

    on(
        MarginCalculatorApiActions.facilityLoadedSuccess,
        (state, { facility, dirtyForm }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                facility,
                housing: {
                    ...state.margin.housing,
                    monthlyHousing: !dirtyForm
                        ? (state.margin.housing.monthlyHousing ?? 0)
                        : state.margin.housing.isAyaHousing
                          ? (facility.maxLodging ?? 0)
                          : 0,
                    defaults: {
                        ...state.margin.housing.defaults,
                        monthlyHousing: facility.maxLodging ?? 0
                    }
                }
            },
            isFacilityInvalid: false,
            orientationAllHoursBillable: facility.orientation?.allHoursBillable ?? false
        })
    ),

    on(
        MarginCalculatorPageActions.facilityNameIsSame,
        (state): MarginCalculatorState => ({
            ...state,
            isFacilityInvalid: false,
            loadFacilityStatus: ApiActionStatus.Success
        })
    ),

    on(
        MarginCalculatorApiActions.findFacilityByNameFail,
        (state): MarginCalculatorState => ({
            ...state,
            isFacilityInvalid: true
        })
    ),

    on(
        MarginCalculatorApiActions.facilitiesLoadedSuccess,
        (state, { facilities }): MarginCalculatorState => ({
            ...state,
            facilities
        })
    ),

    on(
        MarginCalculatorApiActions.clinicianLoadedSuccess,
        (state, { clinician }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                previousProfessionId: state.margin?.clinician?.profession?.id,
                clinician,
                specialty: clinician?.specialties?.find((s) => s.id === state?.margin?.specialty?.id)
            },
            specialties: clinician?.specialties
        })
    ),

    on(
        MarginCalculatorApiActions.clinicianNamesLoadedSuccess,
        (state, { clinicians }): MarginCalculatorState => ({
            ...state,
            cliniciansByName: clinicians
        })
    ),

    on(
        MarginCalculatorApiActions.clinicianEmailsLoadedSuccess,
        (state, { clinicians }): MarginCalculatorState => ({
            ...state,
            cliniciansByEmail: clinicians
        })
    ),

    on(
        MarginCalculatorApiActions.gsaRatesLoadedSuccess,
        (state, { gsaRates }): MarginCalculatorState => ({
            ...state,
            gsaRates
        })
    ),

    on(
        MarginCalculatorApiActions.gsaRatesLoadedFail,
        (state, { message }): MarginCalculatorState => ({
            ...state,
            gsaRates: null,
            errorMessage: message
        })
    ),

    on(
        MarginCalculatorApiActions.getContractDatesLoadedSuccess,
        MarginCalculatorApiActions.getContractEndDateLoadedSuccess,
        MarginCalculatorPageActions.setEndDate,
        (state, { endDate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                endDate,
                housing: {
                    ...state.margin.housing,
                    moveOutDate: HousingStateHelpers.getMoveOutDate(endDate)?.toLocaleDateString()
                }
            }
        })
    ),

    on(
        MarginCalculatorApiActions.reimbursementsLoadedSuccess,
        (state, { reimbursements }): MarginCalculatorState => ({
            ...state,
            reimbursements
        })
    ),

    on(
        MarginCalculatorApiActions.reimbursementsLoadedFail,
        (state, { message }): MarginCalculatorState => ({
            ...state,
            reimbursements: null,
            errorMessage: message
        })
    ),

    on(
        PayBillingActions.revealPayBillingDetailsClicked,
        (state): MarginCalculatorState => ({
            ...state,
            marginFormReveal: FORM_NAMES.facility_billing_details
        })
    ),

    on(
        PayBillingActions.revealStipendDetailsClicked,
        (state): MarginCalculatorState => ({
            ...state,
            marginFormReveal: FORM_NAMES.stipend_details
        })
    ),

    on(
        ReimbursementsActions.revealReimbursementsDetailsClicked,
        (state): MarginCalculatorState => ({
            ...state,
            marginFormReveal: FORM_NAMES.reimbursement_details
        })
    ),

    on(
        HousingActions.revealHousingDetailsClicked,
        (state): MarginCalculatorState => ({
            ...state,
            marginFormReveal: FORM_NAMES.housing_details
        })
    ),

    on(
        InsuranceActions.revealInsuranceDetailsClicked,
        (state): MarginCalculatorState => ({
            ...state,
            marginFormReveal: FORM_NAMES.insurance_details
        })
    ),

    on(
        AdditionalCostsActions.revealAdditionalCostsDetailsClicked,
        (state): MarginCalculatorState => ({
            ...state,
            marginFormReveal: FORM_NAMES.addtl_costs_details
        })
    ),

    on(
        MarginCalculatorPageActions.revealCalculationDetails,
        (state): MarginCalculatorState => ({
            ...state,
            marginFormReveal: FORM_NAMES.calculation_details
        })
    ),

    on(
        MarginCalculatorPageActions.revealMarginDifferencesDetails,
        (state): MarginCalculatorState => ({
            ...state,
            marginFormReveal: FORM_NAMES.margins_differences_details
        })
    ),

    on(
        MarginCalculatorPageActions.revealVacationDetails,
        (state): MarginCalculatorState => ({
            ...state,
            marginFormReveal: FORM_NAMES.vacation_details
        })
    ),

    on(
        MarginCalculatorPageActions.revealPayPackageDetails,
        (state): MarginCalculatorState => ({
            ...state,
            marginFormReveal: FORM_NAMES.pay_package
        })
    ),

    on(
        MarginCalculatorPageActions.setMargin,
        (state, { margin }): MarginCalculatorState => ({
            ...state,
            margin: MarginCalculatorStateHelpers.convertMarginToState(margin)
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginId,
        (state, { marginId }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, id: marginId }
        })
    ),

    on(
        MarginCalculatorPageActions.setContractId,
        (state, { contractId }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, contractId }
        })
    ),

    on(
        MarginCalculatorPageActions.setClinician,
        (state, { clinician }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, clinician }
        })
    ),

    on(
        MarginCalculatorPageActions.setFacility,
        (state, { facility }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, facility }
        })
    ),

    on(
        MarginCalculatorPageActions.setBaseBillingRate,
        (state, { baseBillingRate }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, baseBillingRate }
        })
    ),

    on(
        MarginCalculatorPageActions.baseBillingRateChanged,
        (state, { rate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                baseBillingRate: {
                    ...state.margin.baseBillingRate,
                    value: rate,
                    resetValidation: false,
                    resetChargebumpOncallBillRate: true
                }
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setBaseBillingRateAlt,
        (state, { baseBillingRateAlt }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, baseBillingRateAlt }
        })
    ),

    on(
        MarginCalculatorComputationActions.resetBaseBillingRateAlt,
        (state): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                baseBillingRateAlt: { value: 0, resetValidation: false } as BaseBillingRate
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setBaseBillingRateAltValue,
        (state, { baseBillingRateAltValue }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                baseBillingRateAlt: {
                    ...state.margin.baseBillingRateAlt,
                    value: baseBillingRateAltValue
                }
            }
        })
    ),

    on(
        MarginCalculatorApiActions.billingRatesLoadedSuccess,
        (state, { billingRates }): MarginCalculatorState => ({
            ...state,
            billingRates
        })
    ),

    on(
        MarginCalculatorApiActions.specialtyLoadedSuccess,
        (state, { specialty }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, specialty },
            specialties: [specialty]
        })
    ),

    on(
        MarginCalculatorPageActions.specialtyIdSelected,
        (state, { specialtyId }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, specialty: state.specialties?.find((s) => s.id === specialtyId) }
        })
    ),

    on(
        MarginCalculatorPageActions.specialtySelected,
        (state, { specialty }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, specialty }
        })
    ),

    on(
        MarginCalculatorPageActions.setPendingOfficialOffer,
        (state, { pendingOfficialOffer }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, pendingOfficialOffer }
        })
    ),

    on(
        MarginCalculatorPageActions.setStartDate,
        (state, { startDate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                startDate,
                housing: {
                    ...state.margin.housing,
                    moveInDate: HousingStateHelpers.getMoveInDate(startDate)?.toLocaleDateString()
                }
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setHalfwayDate,
        (state, { halfwayDate }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, halfwayDate }
        })
    ),

    on(
        MarginCalculatorPageActions.vacationSelected,
        (state, { isVacation }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, isVacation }
        })
    ),

    on(
        AdditionalCostsActions.setAYABooster,
        (state, { ayaBooster }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, ayaBooster }
        })
    ),

    on(
        AdditionalCostsActions.setInternalCosts,
        (state, { internalCosts }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, internalCosts }
        })
    ),

    on(
        PayBillingActions.setMarginPayRate,
        MarginCalculatorPageActions.onMarginPayRateChanged,
        (state, { payRate }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, marginPayRate: payRate }
        })
    ),

    on(
        ReimbursementsActions.setRelocationType,
        (state, { relocationType }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, relocationType }
        })
    ),

    on(
        MarginCalculatorPageActions.setIsExtension,
        (state, { isExtension }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, isExtension }
        })
    ),

    on(
        ReimbursementsActions.setRelocationBeginning,
        (state, { relocationTo }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                relocationTo,
                relocation: new Big(relocationTo).add(state.margin.relocationFrom ?? 0).toNumber()
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setShiftType,
        (state, { altShift }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                altShift,
                hoursAlt: altShift === 2 ? state.margin?.hours : 12,
                shiftsAlt: altShift === 2 ? state.margin?.shifts : 3,
                shiftTimeAlt: 3
            }
        })
    ),

    on(
        ReimbursementsActions.setRelocationEnd,
        (state, { relocationFrom }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                relocationFrom,
                relocation: new Big(relocationFrom).add(state.margin.relocationTo ?? 0).toNumber()
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setIsPendingFacilityApproval,
        (state, { isPendingFacilityApproval }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, isPendingFacilityApproval }
        })
    ),

    on(
        ReimbursementsActions.setLicenseAmount,
        (state, { code, amount }): MarginCalculatorState => ({
            ...state,
            reimbursements: {
                ...state.reimbursements,
                stateLicensureReimbursements: state.reimbursements.stateLicensureReimbursements.map((license) =>
                    license.code === code ? { ...license, updatedCost: amount } : license
                )
            }
        })
    ),

    on(ReimbursementsActions.licenseUnselected, (state, { code }): MarginCalculatorState => {
        const existing = state.margin.reimbursements.stateLicensures.find((lic) => lic.code === code);
        return {
            ...state,
            reimbursements: {
                ...state.reimbursements,
                stateLicensureReimbursements: state.reimbursements.stateLicensureReimbursements.map((license) =>
                    license.code === code ? { ...license, updatedCost: existing?.initialCost } : license
                )
            }
        };
    }),

    on(
        ReimbursementsActions.setLicenses,
        (state, { licenses }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    stateLicensures: licenses
                }
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setShiftHours,
        (state, { hours }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, hours }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltShiftHours,
        (state, { hoursAlt }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, hoursAlt }
        })
    ),

    on(
        MarginCalculatorPageActions.setShifts,
        (state, { shifts }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shifts }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltShifts,
        (state, { shiftsAlt }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftsAlt }
        })
    ),

    on(
        MarginCalculatorPageActions.setShiftTime,
        (state, { shiftTime }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftTime }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltShiftTime,
        (state, { shiftTimeAlt }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftTimeAlt }
        })
    ),

    on(
        MarginCalculatorPageActions.setVacationDates,
        (state, { dates }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                vacationDates: dates.length > 0 ? dates.map((date) => dayjs(date).format('MM/DD/YYYY')).join(';') : '',
                isVacation: dates.length > 0
            }
        })
    ),

    on(
        ReimbursementsActions.setCertifications,
        (state, { certifications }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    educationReimbursements: certifications
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setCertificationName,
        (state, { id, name }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    certificationTextOther:
                        id === CERT_OTHER_ID ? name : state.margin.reimbursements?.certificationTextOther,
                    educationReimbursements: state.margin.reimbursements?.educationReimbursements?.map((cert) =>
                        cert.id === id ? { ...cert, name } : cert
                    )
                }
            }
        })
    ),

    on(ReimbursementsActions.setCertificationAmount, (state, { id, amount }): MarginCalculatorState => {
        const itemToUpdate = [
            {
                ...(state.margin.reimbursements?.educationReimbursements?.find((cert) => cert.id === id) ??
                    state.reimbursements.educationReimbursements.find((cert) => cert.id === id))
            }
        ];

        return {
            ...state,
            reimbursements: {
                ...state.reimbursements,
                educationReimbursements: state.reimbursements.educationReimbursements.map((cert) =>
                    cert.id === id ? { ...cert, amount } : cert
                )
            },
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    certificationValueOther:
                        id === CERT_OTHER_ID ? amount : state.margin.reimbursements?.certificationValueOther,
                    educationReimbursements:
                        id === CERT_OTHER_ID
                            ? state.margin.reimbursements?.educationReimbursements
                            : [
                                  ...(state.margin.reimbursements?.educationReimbursements?.filter(
                                      (cert) => cert.id !== id
                                  ) || []),
                                  ...itemToUpdate.map((cert) => ({ ...cert, amount, selected: true }))
                              ]
                }
            }
        };
    }),

    on(
        MarginCalculatorPageActions.setWeeks,
        (state, { weeks }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, weeks, lastWeeksLength: weeks }
        })
    ),

    on(
        ReimbursementsActions.setFacilityPaidBonus,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    facilityPaidBonus: amount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setAyaPaidBonus,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    ayaPaidBonus: amount
                }
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setBookingWeeks,
        (state, { bookingWeeks }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, bookingWeeks }
        })
    ),

    on(
        ReimbursementsActions.setDiscretionaryBonus,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    discretionaryBonus: amount,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'discretionary'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setReferralBonus,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    referralBonus: amount,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'referral'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setOneTimeCarRental,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    carRentalOneTime: amount,
                    rentalCar: 0,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'oneTimeCarRental'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setOneTimeFingerprinting,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    fingerPrintingOneTime: amount,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'oneTimeFingerprinting'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setOneTimeParking,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    parkingOneTime: amount,
                    parkingMonthly: 0,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'oneTimeParking'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setOneTimeScrubs,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    scrubsOneTime: amount,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'oneTimeScrubs'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setOneTimeTaxable,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    taxableModulesOneTime: amount,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'oneTimeTaxable'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setHasOneTimeCustomReimbursement,
        (state): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    hasOneTimeCustomReimbursement: true
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setOneTimeCustomReimbursementTitle,
        (state, { title }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    oneTimeCustomReimbursementTitle: title
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setOneTimeCustomReimbursementAmount,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    oneTimeCustomReimbursementAmount: amount,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'oneTimeCustomReimbursement'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setMonthlyCarRental,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    rentalCar: amount,
                    carRentalOneTime: 0,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'monthlyCarRental'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setMonthlyParking,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    parkingMonthly: amount,
                    parkingOneTime: 0,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'monthlyParking'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setHasMonthlyCustomReimbursement,
        (state): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    hasMonthlyCustomReimbursement: true
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setMonthlyCustomReimbursementTitle,
        (state, { title }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    monthlyCustomReimbursementTitle: title
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setMonthlyCustomReimbursementAmount,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    monthlyCustomReimbursementAmount: amount,
                    pickledAmount:
                        state.margin.reimbursements?.selectedPickleTitle === 'monthlyCustomReimbursement'
                            ? amount
                            : state.margin.reimbursements?.pickledAmount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setPayTo,
        (state, { payTo }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    payToBonus: payTo
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setForReferring,
        (state, { forReferring }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    forReferringBonus: forReferring
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setSelectedPickle,
        (state, { controlName, amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    selectedPickleTitle: controlName,
                    pickledAmount: amount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.setPickledAmount,
        (state, { amount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    pickledAmount: amount
                }
            }
        })
    ),

    on(
        ReimbursementsActions.removeSelectedPickle,
        (state): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                reimbursements: {
                    ...state.margin.reimbursements,
                    selectedPickleTitle: '',
                    pickledAmount: 0
                }
            }
        })
    ),

    on(
        MarginCalculatorPageActions.resetWeeks,
        (state): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, weeks: state.margin.lastWeeksLength }
        })
    ),

    on(
        MarginCalculatorPageActions.setContractDays,
        (state, { contractDays }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, contractDays, lastDaysLength: contractDays }
        })
    ),

    on(
        MarginCalculatorPageActions.resetContractDays,
        (state): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, contractDays: state.margin.lastDaysLength }
        })
    ),

    on(
        MarginCalculatorPageActions.setTimeFrame,
        (state, { shiftH, shiftM, shiftToH, shiftToM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftH, shiftM, shiftToH, shiftToM }
        })
    ),

    on(
        MarginCalculatorPageActions.setStartTimeFrameHours,
        (state, { shiftH }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftH }
        })
    ),

    on(
        MarginCalculatorPageActions.setStartTimeFrameMinutes,
        (state, { shiftM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftM }
        })
    ),

    on(
        MarginCalculatorPageActions.setEndTimeFrame,
        (state, { shiftToH, shiftToM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftToH, shiftToM }
        })
    ),

    on(
        MarginCalculatorPageActions.setEndTimeFrameHours,
        (state, { shiftToH }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftToH }
        })
    ),

    on(
        MarginCalculatorPageActions.setEndTimeFrameMinutes,
        (state, { shiftToM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftToM }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltTimeFrame,
        (state, { shiftAltH, shiftAltM, shiftAltToH, shiftAltToM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftAltH, shiftAltM, shiftAltToH, shiftAltToM }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltStartTimeFrameHours,
        (state, { shiftAltH }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftAltH }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltStartTimeFrameMinutes,
        (state, { shiftAltM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftAltM }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltEndTimeFrame,
        (state, { shiftAltToH, shiftAltToM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftAltToH, shiftAltToM }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltEndTimeFrameHours,
        (state, { shiftAltToH }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftAltToH }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltEndTimeFrameMinutes,
        (state, { shiftAltToM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftAltToM }
        })
    ),

    on(
        MarginCalculatorPageActions.setAndOrBillingRule,
        (state, { andOr }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, andOr }
        })
    ),

    on(
        PayBillingActions.overtimeRulesChanged,
        (state, { overtimeHours, billDailyOtAfter, billDorW, billDorWM, andOr }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                overtimeHours: overtimeHours || 0,
                billDailyOtAfter: billDailyOtAfter || 0,
                billDorW: billDorW || ControlDefaults.EmptySelect,
                billDorWM: billDorWM || ControlDefaults.EmptySelect,
                andOr: andOr || ControlDefaults.EmptySelect
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setFirstOvertimeHours,
        (state, { overtimeHours }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, overtimeHours }
        })
    ),

    on(
        MarginCalculatorPageActions.setSecondOvertimeHours,
        (state, { billDailyOtAfter }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, billDailyOtAfter }
        })
    ),

    on(
        MarginCalculatorPageActions.setFirstOvertimeFrequency,
        (state, { billDorW }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, billDorW }
        })
    ),

    on(
        MarginCalculatorPageActions.setSecondOvertimeFrequency,
        (state, { billDorWM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, billDorWM }
        })
    ),

    on(
        MarginCalculatorPageActions.setFirstOrientationHours,
        (state, { orientationValue }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, orientationFirstHours: orientationValue }
        })
    ),

    on(
        MarginCalculatorPageActions.setNextOrientationHours,
        (state, { orientationNextValue }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, orientationNextValue }
        })
    ),

    on(
        MarginCalculatorPageActions.setFirstOrientationAmount,
        (state, { orientationAmount }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, orientationAmount }
        })
    ),

    on(
        MarginCalculatorPageActions.setNextOrientationAmount,
        (state, { orientationNextAmount }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, orientationNextAmount }
        })
    ),

    on(
        MarginCalculatorPageActions.setFirstOrientationType,
        (state, { orientationType }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, orientationType }
        })
    ),

    on(
        MarginCalculatorPageActions.setNextOrientationType,
        (state, { orientationNextType }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, orientationNextType }
        })
    ),

    on(
        MarginCalculatorPageActions.setOrientationAllHoursBillable,
        (state, { orientationAllHoursBillable }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, orientationAllHoursBillable }
        })
    ),

    on(
        MarginCalculatorPageActions.setRegularPayHours,
        (state, { regPayHrs }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, regPayHrs }
        })
    ),

    on(
        MarginCalculatorPageActions.setOvertimePayHours,
        (state, { otPayHrs }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, otPayHrs }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltRegularPayHours,
        (state, { altRegPayHrs }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, altRegPayHrs }
        })
    ),

    on(
        MarginCalculatorPageActions.setAltOvertimePayHours,
        (state, { altOtPayHrs }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, altOtPayHrs }
        })
    ),

    on(
        MarginCalculatorPageActions.setPayHours,
        (state, { regPayHrs, otPayHrs, altRegPayHrs, altOtPayHrs }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, regPayHrs, otPayHrs, altRegPayHrs, altOtPayHrs }
        })
    ),

    on(
        MarginCalculatorPageActions.setBillingRegularHours,
        (state, { billingRegHours }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, billingRegHours }
        })
    ),

    on(
        MarginCalculatorPageActions.setBillingOvertimeHours,
        (state, { billingOtHours }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, billingOtHours }
        })
    ),

    on(
        MarginCalculatorPageActions.setWeeklyHours,
        (state, { weeklyHours }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, weeklyHours }
        })
    ),

    on(
        MarginCalculatorApiActions.housewareListLoadedSuccess,
        (state, { housewares }): MarginCalculatorState => ({
            ...state,
            housewares
        })
    ),

    on(
        HousingActions.setHousingSelected,
        (state, { housingSelected }): MarginCalculatorState => ({
            ...state,
            marginFormReveal: housingSelected ? FORM_NAMES.housing_details : state.marginFormReveal,
            margin: {
                ...state.margin,
                housing: {
                    ...state.margin.housing,
                    isAyaHousing: housingSelected,
                    housingType: HousingStateHelpers.getHousingType(1),
                    monthlyHousing: housingSelected ? (state.margin.facility?.maxLodging ?? 0) : 0
                }
            }
        })
    ),

    on(
        HousingActions.setDefaultHousingForWFDSample,
        (state, { housingSelected }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: {
                    ...state.margin.housing,
                    isAyaHousing: housingSelected,
                    housingType: HousingStateHelpers.getHousingType(2),
                    monthlyHousing: housingSelected ? (state.margin.facility?.maxLodging ?? 0) : 0
                }
            }
        })
    ),

    on(
        HousingActions.resetHousingFields,
        (state, { moveInDate, moveOutDate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: {
                    ...state.margin.housing,
                    moveInDate: moveInDate?.toLocaleDateString(),
                    moveOutDate: moveOutDate?.toLocaleDateString()
                }
            }
        })
    ),

    on(
        HousingActions.setHousingType,
        (state, { housingTypeId }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: HousingStateHelpers.getHousingTypeOptions(
                    state.margin.housing,
                    housingTypeId,
                    state.margin.startDate,
                    state.margin.endDate
                )
            }
        })
    ),

    on(
        HousingActions.setContinuedHousing,
        (state, { continuedHousing }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, housing: { ...state.margin.housing, continuedHousing } }
        })
    ),

    on(
        HousingActions.setMonthlyRent,
        (state, { monthlyHousing, extendedStayHotel }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, housing: { ...state.margin.housing, monthlyHousing, extendedStayHotel } }
        })
    ),

    on(
        HousingActions.setMoveInDate,
        (state, { moveInDate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, moveInDate: moveInDate?.toLocaleDateString() }
            }
        })
    ),

    on(
        HousingActions.setMoveOutDate,
        (state, { moveOutDate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, moveOutDate: moveOutDate?.toLocaleDateString() }
            }
        })
    ),

    on(
        HousingActions.setBedrooms,
        (state, { bedrooms }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: {
                    ...state.margin.housing,
                    bedrooms,
                    moveOutExpenses: HousingStateHelpers.getMoveOutExpenses(bedrooms),
                    furnitureDelivery: HousingStateHelpers.getFurnitureDelivery(bedrooms),
                    furniture: HousingStateHelpers.getFurniture(bedrooms),
                    utilities: HousingStateHelpers.getUtilities(bedrooms)
                }
            }
        })
    ),

    on(
        HousingActions.setMoveOutExpenses,
        (state, { moveOutExpenses }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, moveOutExpenses }
            }
        })
    ),

    on(
        HousingActions.setFurnitureDelivery,
        (state, { furnitureDelivery }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, furnitureDelivery }
            }
        })
    ),

    on(
        HousingActions.setFurniture,
        (state, { furniture }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, furniture }
            }
        })
    ),

    on(
        HousingActions.setUtilities,
        (state, { utilities }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, utilities }
            }
        })
    ),

    on(
        HousingActions.setHousewareSelected,
        (state, { housewareId, selected, duplicationCheck }): MarginCalculatorState => {
            const alreadyExists = state.margin.housing.housewares.some((x) => x.id === housewareId);
            return {
                ...state,
                margin: {
                    ...state.margin,
                    housing: {
                        ...state.margin.housing,
                        housewares: selected
                            ? alreadyExists && duplicationCheck
                                ? state.margin.housing.housewares
                                : [...state.margin.housing.housewares, { id: housewareId, selected: true }]
                            : state.margin.housing.housewares.filter((x) => x.id !== housewareId)
                    }
                }
            };
        }
    ),

    on(
        HousingActions.setOtherHousewareTitle,
        (state, { title }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: {
                    ...state.margin.housing,
                    housewares: state.margin.housing.housewares.map((h) =>
                        h.id === HOUSEWARE_OTHER_ID ? { ...h, title } : h
                    )
                }
            }
        })
    ),

    on(
        HousingActions.setOtherHousewareCost,
        (state, { cost }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: {
                    ...state.margin.housing,
                    housewares: state.margin.housing.housewares.map((h) =>
                        h.id === HOUSEWARE_OTHER_ID ? { ...h, avgCost: cost } : h
                    )
                }
            }
        })
    ),

    on(
        HousingActions.setFamilyMemberSelected,
        (state, { selected }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, isFamilyIncluded: selected }
            }
        })
    ),

    on(
        HousingActions.setFamilyMemberRelationship,
        (state, { relationship }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, familyRelationship: relationship }
            }
        })
    ),

    on(
        HousingActions.setFamilyMemberName,
        (state, { name }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, familyName: name }
            }
        })
    ),

    on(
        HousingActions.setFamilyMemberAge,
        (state, { age }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, familyAge: age }
            }
        })
    ),

    on(
        HousingActions.setFamilyMemberAdditionalNotes,
        (state, { additionalNotes }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, familyNotes: additionalNotes }
            }
        })
    ),

    on(
        HousingActions.setPetSelected,
        (state, { selected }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, isPetIncluded: selected }
            }
        })
    ),

    on(
        HousingActions.setPetType,
        (state, { petType }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, petType }
            }
        })
    ),

    on(
        HousingActions.setPetBreed,
        (state, { breed }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, petBreed: breed }
            }
        })
    ),

    on(
        HousingActions.setPetWeight,
        (state, { weight }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, petWeight: weight }
            }
        })
    ),

    on(
        HousingActions.setPetAge,
        (state, { age }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, petAge: age }
            }
        })
    ),

    on(
        HousingActions.setFurnitureUtilitiesExpenses,
        (state, { moveOutExpenses, furnitureDelivery, furniture, utilities }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                housing: { ...state.margin.housing, moveOutExpenses, furnitureDelivery, furniture, utilities }
            }
        })
    ),

    on(
        MarginCalculatorApiActions.insuranceInfoLoadedSuccess,
        (state, { insuranceInfo }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, insuranceInfo }
        })
    ),

    on(
        PayBillingActions.setPremiumRate,
        (state, { payRate }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, premiumRate: payRate }
        })
    ),

    on(
        PayBillingActions.setOvertimeRate,
        (state, { overTimeRate }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, overTimeRate }
        })
    ),

    on(
        PayBillingActions.setOvertimeRateAlt,
        (state, { overTimeRateAlt }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, overTimeRateAlt }
        })
    ),

    on(
        PayBillingActions.setTaxableStipend,
        (state, { taxableStipend }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, taxableStipend }
        })
    ),

    on(
        PayBillingActions.setMealStipend,
        (state, { mealStipend }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    mealStipend
                }
            }
        })
    ),

    on(
        PayBillingActions.setMealStipendAndRelatedValues,
        (state, { mealStipend, mealStipendPerShift, mealStipendPerDay, weeklyStipends }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    mealStipend,
                    mealStipendPerShift,
                    mealStipendPerDay,
                    weeklyStipends
                }
            }
        })
    ),

    on(
        PayBillingActions.setCalculatedStipendValues,
        (state, { mealStipend, housingStipend, weeklyStipends }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    mealStipend,
                    housingStipend,
                    weeklyStipends
                }
            }
        })
    ),

    on(
        MarginCalculatorApiActions.insuranceEffectiveDatesLoadedSuccess,
        (state, { marginStateInsuranceEffectiveDates }): MarginCalculatorState => ({
            ...state,
            insuranceEffectiveDates: {
                newEffectiveDate: marginStateInsuranceEffectiveDates.newEffectiveDate,
                oldEffectiveDate: marginStateInsuranceEffectiveDates.oldEffectiveDate,
                releaseNewEffectiveDate: marginStateInsuranceEffectiveDates.releaseNewEffectiveDate
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginMedicalDays,
        (state, { noOfMedicalDays }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, noOfMedicalDays }
        })
    ),

    on(
        PayBillingActions.setHousingStipend,
        (state, { housingStipend }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    housingStipend
                }
            }
        })
    ),

    on(
        PayBillingActions.setHousingStipendAndRelatedValues,
        (
            state,
            { housingStipend, housingStipendPerShift, housingStipendPerDay, weeklyStipends }
        ): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    housingStipend,
                    housingStipendPerShift,
                    housingStipendPerDay,
                    weeklyStipends
                }
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginDentalDays,
        (state, { noOfDentalDays }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, noOfDentalDays }
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginMedical,
        (state, { isMedical }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, isMedical }
        })
    ),

    on(
        PayBillingActions.setStipendsPerContract,
        (state, { stipendsPerContract }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    stipendsPerContract
                }
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginDental,
        (state, { isDental }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, isDental }
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginMedicalFlagAndDays,
        (state, { marginMedicalDays, marginMedical }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, marginMedicalDays, marginMedical }
        })
    ),

    on(
        PayBillingActions.setMaxMeal,
        (state, { maxMeals }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    maxMeals
                }
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginDentalFlagAndDays,
        (state, { marginDentalDays, marginDental }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, marginDentalDays, marginDental }
        })
    ),

    on(
        PayBillingActions.setMaxHousing,
        (state, { maxHousing }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    maxHousing
                }
            }
        })
    ),

    on(
        PayBillingActions.setMaxStipends,
        (state, { maxStipends }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    maxStipends
                }
            }
        })
    ),

    on(
        PayBillingActions.setWeeklyStipends,
        (state, { weeklyStipends }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    weeklyStipends
                }
            }
        })
    ),

    on(
        PayBillingActions.setBlendedPayRate,
        (state, { payRate }): MarginCalculatorState => ({
            ...state,
            blendedPayRate: payRate
        })
    ),

    on(
        MarginCalculatorPageActions.setPremiumMargin,
        (state, { premiumMargin }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, premiumMargin }
        })
    ),

    on(
        MarginCalculatorPageActions.setInsuranceNeeded,
        MarginCalculatorComputationActions.insuranceNeededComputedSuccess,
        (state, { isBenefit }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                isBenefit,
                medicalCoverage: MarginCalculatorStateHelpers.getBenefitCoverage(isBenefit),
                dentalCoverage: MarginCalculatorStateHelpers.getBenefitCoverage(isBenefit)
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setIsExtendedBenefits,
        (state, { isExtendedBenefits }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, isExtendedBenefits }
        })
    ),

    on(
        MarginCalculatorComputationActions.isExtendedBenefitsComputedSuccess,
        (state, { isExtendedBenefits }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, isExtendedBenefits }
        })
    ),

    on(
        MarginCalculatorPageActions.setTotalMedicalAndDentalCostCalculated,
        (state, { totalMedicalCostCalculated, totalDentalCostCalculated }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, totalMedicalCostCalculated, totalDentalCostCalculated }
        })
    ),

    on(
        MarginCalculatorPageActions.setTotalInsuranceCostCalculated,
        (state, { totalInsuranceCostCalculated }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, totalInsuranceCostCalculated }
        })
    ),

    on(
        MarginCalculatorPageActions.setMedical,
        (state, { medical }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, medical }
        })
    ),
    on(
        MarginCalculatorPageActions.setDental,
        (state, { dental }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, dental }
        })
    ),
    on(
        MarginCalculatorPageActions.setDentalDays,
        (state, { dentalDays }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, dentalDays }
        })
    ),
    on(
        MarginCalculatorPageActions.setMedicalDays,
        (state, { medicalDays }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, medicalDays }
        })
    ),
    on(
        MarginCalculatorPageActions.setShowShiftAmount,
        (state, { showShiftAmt }): MarginCalculatorState => ({
            ...state,
            showShiftAmt
        })
    ),
    on(
        PayBillingActions.setOtBillingRulesEnabled,
        (state, { isBillOverTime }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, isBillOverTime }
        })
    ),
    on(
        PayBillingActions.setContractFees,
        (state, { contractFees }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, vmsFees: contractFees }
        })
    ),
    on(
        MarginCalculatorPageActions.setOriginalBillingRate,
        (state, { billingRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                baseBillingRate: {
                    ...state.margin.baseBillingRate,
                    originalBillingRate: billingRate
                }
            }
        })
    ),
    on(
        MarginCalculatorPageActions.setOriginalBillingRateAlt,
        (state, { billingRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                baseBillingRateAlt: {
                    ...state.margin.baseBillingRateAlt,
                    originalBillingRate: billingRate
                }
            }
        })
    ),
    on(
        MarginCalculatorPageActions.setMarginReviewAndSendEnabled,
        MarginCalculatorPageActions.setMarginReviewAndSendForContractRequest,
        (state, { reviewAndSendEnabled }): MarginCalculatorState => ({
            ...state,
            reviewAndSendEnabled
        })
    ),
    on(
        PayBillingActions.setMinPremiumPayRates,
        PayBillingActions.setMinPremiumPayRateOnly,
        (state, { rate }): MarginCalculatorState => ({
            ...state,
            minPremiumPayRate: rate
        })
    ),
    on(
        PayBillingActions.setShiftBonus,
        (state, { shiftBonus }): MarginCalculatorState => ({
            ...state,
            shiftBonus
        })
    ),
    on(
        PayBillingActions.earlyPaymentDiscountChanged,
        (state: MarginCalculatorState, { discountPercent }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                discountPercent
            }
        })
    ),
    on(
        PayBillingActions.extDiscountChanged,
        (state: MarginCalculatorState, { extDiscount }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                extDiscount
            }
        })
    ),
    on(
        PayBillingActions.holidayPayRateChanged,
        (state: MarginCalculatorState, { holidayPayRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                holidayPayRate
            }
        })
    ),
    on(
        PayBillingActions.holidayCallbackPayRateChanged,
        (state: MarginCalculatorState, { holidayPayRate, callbackPayRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                holidayPayRate,
                callbackPayRate
            }
        })
    ),
    on(
        PayBillingActions.holidayBillRateChanged,
        (state: MarginCalculatorState, { holidayBillRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                holidayBillRate
            }
        })
    ),
    on(
        PayBillingActions.onCallPayRateChanged,
        (state: MarginCalculatorState, { onCallPayRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                onCallPayRate
            }
        })
    ),
    on(
        PayBillingActions.onCallBillRateChanged,
        (state: MarginCalculatorState, { onCallBillRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                onCallBillRate
            }
        })
    ),
    on(
        PayBillingActions.callbackPayRateChanged,
        (state: MarginCalculatorState, { callbackPayRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                callbackPayRate
            }
        })
    ),
    on(
        PayBillingActions.callbackBillRateChanged,
        (state: MarginCalculatorState, { callbackBillRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                callbackBillRate
            }
        })
    ),
    on(
        PayBillingActions.chargePayRateChanged,
        (state: MarginCalculatorState, { chargePayRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                chargePayRate
            }
        })
    ),
    on(
        PayBillingActions.chargeBillRateChanged,
        (state: MarginCalculatorState, { chargeBillRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                chargeBillRate
            }
        })
    ),
    on(
        MarginCalculatorApiActions.getContractLengthWithBookingWeeksLoadedSuccess,
        (state, { weeks, lastWeeksLength, bookingWeeks, altBookingWeeks }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, weeks, lastWeeksLength, bookingWeeks, altBookingWeeks }
        })
    ),
    on(
        MarginCalculatorComputationActions.payHoursComputedSuccess,
        (state, { regPayHrs, otPayHrs, altRegPayHrs, altOtPayHrs }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, regPayHrs, otPayHrs, altRegPayHrs, altOtPayHrs }
        })
    ),
    on(
        MarginCalculatorComputationActions.billableHoursComputedSuccess,
        (
            state,
            {
                billingRegHours,
                billingOtHours,
                primaryNonStandardOvertimeBillingHours,
                alternateNonStandardOvertimeBillingHours
            }
        ): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                billingRegHours,
                billingOtHours,
                primaryNonStandardOvertimeBillingHours,
                alternateNonStandardOvertimeBillingHours
            }
        })
    ),
    on(
        MarginCalculatorComputationActions.billingOvertimeEnabledComputedSuccess,
        (state, { isBillOverTime }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, isBillOverTime }
        })
    ),
    on(
        MarginCalculatorComputationActions.baseBillingRateComputedSuccess,
        (state, { baseBillingRate }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, baseBillingRate }
        })
    ),
    on(
        MarginCalculatorComputationActions.baseBillingRateAltComputedSuccess,
        (state, { baseBillingRateAlt }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, baseBillingRateAlt: baseBillingRateAlt }
        })
    ),
    on(
        MarginCalculatorComputationActions.timeFrameComputedSuccess,
        (state, { shiftH, shiftM, shiftToH, shiftToM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftH, shiftM, shiftToH, shiftToM }
        })
    ),
    on(
        MarginCalculatorComputationActions.altTimeFrameComputedSuccess,
        (state, { shiftAltH, shiftAltM, shiftAltToH, shiftAltToM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftAltH, shiftAltM, shiftAltToH, shiftAltToM }
        })
    ),
    on(
        MarginCalculatorComputationActions.endTimeFrameComputedSuccess,
        (state, { shiftToH, shiftToM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftToH, shiftToM }
        })
    ),
    on(
        MarginCalculatorComputationActions.altEndTimeFrameComputedSuccess,
        (state, { shiftAltToH, shiftAltToM }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftAltToH, shiftAltToM }
        })
    ),
    on(
        MarginCalculatorComputationActions.endTimeFrameHoursComputedSuccess,
        (state, { shiftToH }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftToH }
        })
    ),
    on(
        MarginCalculatorComputationActions.altEndTimeFrameHoursComputedSuccess,
        (state, { shiftAltToH }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, shiftAltToH }
        })
    ),
    on(
        MarginCalculatorComputationActions.contractDaysComputedSuccess,
        (state, { contractDays, lastDaysLength }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, contractDays, lastDaysLength }
        })
    ),
    on(
        MarginCalculatorComputationActions.weeklyHoursComputedSuccess,
        (state, { weeklyHours }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, weeklyHours }
        })
    ),
    on(
        MarginCalculatorComputationActions.halfwayDateComputedSuccess,
        (state, { halfwayDate }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, halfwayDate }
        })
    ),

    on(
        PayBillingActions.recalculateOnCallPayRateSuccess,
        (state: MarginCalculatorState, { onCallPayRate, marginOnCallPayRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                onCallPayRate: marginOnCallPayRate ?? onCallPayRate
            }
        })
    ),

    on(
        PayBillingActions.recalculateOnCallBillRateSuccess,
        (state: MarginCalculatorState, { onCallBillRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                onCallBillRate
            }
        })
    ),

    on(
        PayBillingActions.recalculateChargePayRateSuccess,
        (state: MarginCalculatorState, { marginChargePayRate, chargePayRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                chargePayRate: marginChargePayRate ?? chargePayRate
            }
        })
    ),

    on(
        PayBillingActions.recalculateChargeBillRateSuccess,
        (state: MarginCalculatorState, { chargeBillRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                chargeBillRate
            }
        })
    ),

    on(
        PayBillingActions.recalculateHolidayBillRateSuccess,
        (state: MarginCalculatorState, { holidayBillRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                holidayBillRate
            }
        })
    ),

    on(
        PayBillingActions.recalculateCallbackBillRateSuccess,
        (state: MarginCalculatorState, { callbackBillRate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                callbackBillRate
            }
        })
    ),

    on(
        MarginCalculatorApiActions.marginsDifferencesLoadedFail,
        (state, { message }): MarginCalculatorState => ({
            ...state,
            marginsDifferences: null,
            errorMessage: message
        })
    ),

    on(
        MarginCalculatorApiActions.marginsDifferencesLoadedSuccess,
        (state, { marginsDifferences }): MarginCalculatorState => ({
            ...state,
            marginsDifferences
        })
    ),

    on(
        MarginCalculatorPageActions.setTaxRate,
        (state, { taxRate }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, taxRate }
        })
    ),
    on(
        MarginCalculatorApiActions.targetMarginWithExpertiseLoadedSuccess,
        MarginCalculatorApiActions.targetMarginLoadedSuccess,
        MarginCalculatorApiActions.targetMarginWithProfessionLoadedSuccess,
        MarginCalculatorComputationActions.setTargetMargin,
        (state, { targetMargin }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, targetMargin }
        })
    ),

    on(
        MarginCalculatorPageActions.setInsuranceOverrideOption,
        (
            state: MarginCalculatorState,
            { overrideIneligibility, requireOverride, insuranceBenefitTypes }
        ): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                overrideIneligibility,
                requireOverride,
                insuranceBenefitTypes
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setOverrideIneligibility,
        (state: MarginCalculatorState, { overrideIneligibility }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                overrideIneligibility
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setRequireOverride,
        (state: MarginCalculatorState, { requireOverride }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                requireOverride
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setIneligibilityDialog,
        (state: MarginCalculatorState, { overrideIneligibility, isBenefit }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                overrideIneligibility,
                isBenefit
            }
        })
    ),

    /** Save Margin Actions */
    on(
        UpdateMarginActions.resetUpdateStatus,
        (state): MarginCalculatorState => ({
            ...state,
            saveMarginAction: {
                ...state.saveMarginAction,
                status: ApiActionStatus.None
            }
        })
    ),

    on(
        UpdateMarginActions.saveMarginCommonSuccess,
        (state, { marginId }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                id: marginId,
                createdBy: state.userCredentials?.userId
            }
        })
    ),

    on(
        DuplicateMarginActions.duplicateMargin,
        (state, action): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                stipends: {
                    ...state.margin.stipends,
                    mealStipend: new Big(state.margin?.stipends?.mealStipend ?? 0).round(0).toNumber(),
                    housingStipend: new Big(state.margin?.stipends?.housingStipend ?? 0).round(0).toNumber(),
                    weeklyStipends: new Big(state.margin?.stipends?.mealStipend ?? 0)
                        .round(0)
                        .add(state.margin?.stipends?.housingStipend ?? 0)
                        .round(0)
                        .toNumber()
                },
                duplicatedFromMarginId: state.margin.id,
                id: 0,
                targetMargin: null,
                dateCreated: new Date().toLocaleString(),
                createdBy: state.userCredentials?.userId,
                jobId: action.jobId ?? state.margin.jobId,
                openContractRequestId: null,
                originalHospitalId: null,
                offerId: null
            },
            offerAyaPaidBonus: 0
        })
    ),

    on(
        ReviewAndSendActions.createContractRequest,
        DuplicateMarginActions.duplicateMargin,
        PipelineMarginActions.pipelineMargin,
        ReviewAndSendActions.reviewAndSendMargin,
        UpdateMarginActions.saveMargin,
        LinkToOfferActions.linkToOfferSaveMargin,
        (state, action): MarginCalculatorState => ({
            ...state,
            saveMarginAction: {
                ...state.saveMarginAction,
                actionType: action.type,
                status: ApiActionStatus.InProgress
            }
        })
    ),

    on(
        UpdateMarginActions.saveClicked,
        (state, action): MarginCalculatorState => ({
            ...state,
            saveMarginAction: {
                ...state.saveMarginAction,
                actionType: action.type,
                status: ApiActionStatus.None
            }
        })
    ),

    on(
        ReviewAndSendActions.createContractRequestSuccess,
        DuplicateMarginActions.duplicateMarginSuccess,
        PipelineMarginActions.pipelineMarginSuccess,
        ReviewAndSendActions.reviewAndSendMarginSuccess,
        UpdateMarginActions.saveMarginSuccess,
        LinkToOfferActions.linkToOfferMarginSaveSuccess,
        (state): MarginCalculatorState => ({
            ...state,
            saveMarginAction: {
                ...state.saveMarginAction,
                status: ApiActionStatus.Success
            }
        })
    ),

    on(
        PipelineMarginActions.pipelineMarginSuccess,
        (state, { marginContract }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                contractId: marginContract.contractId,
                contractStageCode: marginContract.contractStageCode,
                contractStageDescription: marginContract.contractStageDescription
            }
        })
    ),

    on(
        ReviewAndSendActions.createContractRequestSuccess,
        (state, { contractRequestId }): MarginCalculatorState => ({
            ...state,
            contractRequestId
        })
    ),

    on(
        ReviewAndSendActions.createContractRequestFail,
        DuplicateMarginActions.duplicateMarginFail,
        PipelineMarginActions.pipelineMarginFail,
        ReviewAndSendActions.reviewAndSendMarginFail,
        ReviewAndSendActions.reviewAndSendMarginBlockedByViolations,
        UpdateMarginActions.saveMarginFail,
        UpdateMarginActions.saveConfirmedFailed,
        ValidateFormActions.validateFormBeforeSaveFail,
        LinkToOfferActions.linkToOfferMarginSaveFail,
        (state): MarginCalculatorState => ({
            ...state,
            saveMarginAction: {
                ...state.saveMarginAction,
                status: ApiActionStatus.Failed
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginMedicalInfo,
        (state, { noOfMedicalDays, isMedical }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, noOfMedicalDays, isMedical }
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginDentalInfo,
        (state, { noOfDentalDays, isDental }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, noOfDentalDays, isDental }
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginMedicalAndDentalInfo,
        (state, { noOfDentalDays, isDental, noOfMedicalDays, isMedical }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, noOfDentalDays, isDental, noOfMedicalDays, isMedical }
        })
    ),

    on(
        MarginCalculatorPageActions.clearMarginMedicalAndDentalInfo,
        (state): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                isExtendedBenefits: false,
                noOfDentalDays: 0,
                isDental: false,
                noOfMedicalDays: 0,
                isMedical: false
            }
        })
    ),

    on(
        MarginCalculatorPageActions.showOverrideInsuranceDialog,
        (state: MarginCalculatorState, { insuranceSelectionRecentTrigger }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                insuranceSelectionRecentTrigger
            }
        })
    ),

    on(
        MarginCalculatorPageActions.loadInsuranceInfo,
        (state: MarginCalculatorState, { insuranceSelectionRecentTrigger }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                insuranceSelectionRecentTrigger
            }
        })
    ),

    on(
        MarginCalculatorPageActions.loadFacility,
        MarginCalculatorPageActions.loadFacilityByName,
        (state: MarginCalculatorState): MarginCalculatorState => ({
            ...state,
            loadFacilityStatus: ApiActionStatus.InProgress
        })
    ),

    on(
        MarginCalculatorApiActions.facilityLoadedSuccess,
        MarginCalculatorApiActions.facilityLoadedByNameSuccess,
        (state: MarginCalculatorState): MarginCalculatorState => ({
            ...state,
            loadFacilityStatus: ApiActionStatus.Success
        })
    ),

    on(
        MarginCalculatorApiActions.facilityLoadedFail,
        MarginCalculatorApiActions.findFacilityByNameFail,
        (state: MarginCalculatorState): MarginCalculatorState => ({
            ...state,
            loadFacilityStatus: ApiActionStatus.Failed
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginNotes,
        (state, { internalCostNotes }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, internalCostNotes }
        })
    ),

    on(
        MarginCalculatorApiActions.minimumWageDetailsLoadedSuccess,
        (state, { minimumWageDetails }): MarginCalculatorState => ({
            ...state,
            minimumWageDetails
        })
    ),

    on(
        MarginCalculatorApiActions.minimumWageDetailsLoadedFail,
        (state, { message }): MarginCalculatorState => ({
            ...state,
            minimumWageDetails: null,
            errorMessage: message
        })
    ),

    on(
        MarginCalculatorPageActions.setImportedFromMarginId,
        (state, { importedFromMarginId }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, importedFromMarginId }
        })
    ),

    on(
        MarginCalculatorPageActions.clearBillingRates,
        MarginCalculatorApiActions.billingRatesLoadedFail,
        (state): MarginCalculatorState => ({
            ...state,
            billingRates: undefined
        })
    ),
    on(
        MarginCalculatorUserInterfaceActions.setUserInterfaceState,
        (state, { userInterfaceState }): MarginCalculatorState => ({
            ...state,
            userInterfaceState
        })
    ),
    on(
        MarginCalculatorUserInterfaceActions.setUserInterfaceSpecialtyControl,
        (state, { specialtyControl }): MarginCalculatorState => ({
            ...state,
            userInterfaceState: {
                ...state.userInterfaceState,
                specialtyControl
            }
        })
    ),
    on(
        MarginCalculatorUserInterfaceActions.setUserInterfaceInitialSpecialtyChanged,
        (state, { isInitialSpecialtyChanged, specialtyId }): MarginCalculatorState => ({
            ...state,
            margin: { ...state.margin, specialty: state.specialties?.find((s) => s.id === specialtyId) },
            userInterfaceState: {
                ...state.userInterfaceState,
                specialtyControl: {
                    ...state.userInterfaceState.specialtyControl,
                    isInitialSpecialtyChanged
                }
            }
        })
    ),

    on(
        MarginCalculatorUserInterfaceActions.setUserInterfaceInitialFacilityChanged,
        (state, { isInitialFacilityChanged }): MarginCalculatorState => ({
            ...state,
            userInterfaceState: {
                ...state.userInterfaceState,
                facilityControl: {
                    ...state.userInterfaceState.facilityControl,
                    isInitialFacilityChanged
                }
            }
        })
    ),

    on(
        MarginCalculatorUserInterfaceActions.setUserInterfaceInitialClinicianChanged,
        (state, { isInitialClinicianChanged }): MarginCalculatorState => ({
            ...state,
            userInterfaceState: {
                ...state.userInterfaceState,
                clinicianControl: {
                    ...state.userInterfaceState.clinicianControl,
                    isInitialClinicianChanged
                }
            }
        })
    ),

    on(
        MarginCalculatorPageActions.clearMarginsDifferences,
        (state): MarginCalculatorState => ({
            ...state,
            marginsDifferences: undefined
        })
    ),

    on(
        MarginCalculatorPageActions.linkToOfferDialogClosed,
        (state): MarginCalculatorState => ({
            ...state,
            continueWithoutLinking: true
        })
    ),

    on(
        MarginCalculatorApiActions.resetApiCallResponses,
        (state: MarginCalculatorState): MarginCalculatorState => ({
            ...state,
            apiCallResponses: undefined
        })
    ),

    on(
        MarginCalculatorApiActions.resetGetPremiumBillRateResponseState,
        (state: MarginCalculatorState): MarginCalculatorState => ({
            ...state,
            apiCallResponses: {
                ...state.apiCallResponses,
                getPremiumBillRateApiResponse: undefined
            }
        })
    ),

    on(
        UpdateMarginActions.saveClicked,
        ReviewAndSendActions.reviewAndSendClicked,
        PipelineMarginActions.pipelineMargin,
        LinkToOfferActions.linkToOfferSaveMargin,
        UpdateMarginActions.linkSampleMarginToJob,
        (state: MarginCalculatorState): MarginCalculatorState => ({
            ...state,
            isBusy: true
        })
    ),

    on(
        // Save completed
        UpdateMarginActions.saveMarginSuccess,
        UpdateMarginActions.saveMarginFail,
        UpdateMarginActions.saveConfirmedFailed,
        UpdateMarginActions.cancelSaveMargin,
        ValidateFormActions.validateFormBeforeSaveFail,
        LinkToOfferActions.linkToOfferMarginSaveSuccess,
        LinkToOfferActions.linkToOfferMarginSaveFail,

        // Review + Send completed
        MarginCalculatorApiActions.getContractRequestCountFail,
        ReviewAndSendActions.reviewAndSendMarginFail,

        // Pipeline completed
        PipelineMarginActions.pipelineMarginSuccess,
        PipelineMarginActions.pipelineMarginFail,

        // Existing Contract Request Dialog canceled
        ReviewAndSendActions.existingContractRequestDialogCanceled,

        // Money Movement flyout
        ReviewAndSendActions.reviewAndSendMarginBlockedByViolations,

        //Link Margin to Job
        UpdateMarginActions.linkSampleMarginToJobSuccess,
        UpdateMarginActions.linkSampleMarginToJobFail,

        (state: MarginCalculatorState): MarginCalculatorState => ({
            ...state,
            isBusy: false
        })
    ),

    on(
        MarginCalculatorApiActions.professionWithSpecialtiesLoadedSuccess,
        (state, { professionWithSpecialties }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                clinician: {
                    ...state.margin.clinician,
                    profession: professionWithSpecialties.profession,
                    specialties: professionWithSpecialties.specialties
                }
            },
            specialties: professionWithSpecialties.specialties
        })
    ),

    on(
        MarginCalculatorPageActions.resetOrientationIndicator,
        (state): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                orientationType: '',
                orientationNextType: ''
            }
        })
    ),

    on(
        PayBillingActions.setCallbackIncluded,
        (state, { callbackPayRateIncluded }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                callbackPayRateIncluded
            }
        })
    ),

    on(
        MarginCalculatorApiActions.facilityContractRulesLoadedSucess,
        (state, { facilityContractRules }): MarginCalculatorState => ({
            ...state,
            facilityContractRules
        })
    ),

    on(
        MarginCalculatorApiActions.changeOfContractMarginIdLoadedSuccess,
        (state, { changeOfContractMarginId }): MarginCalculatorState => ({
            ...state,
            changeOfContractMarginId
        })
    ),

    on(
        MarginCalculatorApiActions.moneyMovementValidationsLoadedSuccess,
        (state, { moneyMovementValidations }): MarginCalculatorState => ({
            ...state,
            moneyMovementValidations
        })
    ),

    on(
        MarginCalculatorApiActions.offerAyaPaidBonusLoadedSuccess,
        (state, { offerAyaPaidBonus }): MarginCalculatorState => ({
            ...state,
            offerAyaPaidBonus
        })
    ),

    on(
        MarginCalculatorComputationActions.resetMarginId,
        (state): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                id: state.margin.duplicatedFromMarginId ?? 0
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setMedicalEffectiveDate,
        (state, { medicalEffectiveDate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                medicalEffectiveDate
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setDentalEffectiveDate,
        (state, { dentalEffectiveDate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                dentalEffectiveDate
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setMedicalAndDentalEffectiveDate,
        (state, { medicalEffectiveDate, dentalEffectiveDate }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                medicalEffectiveDate,
                dentalEffectiveDate
            }
        })
    ),

    on(
        MarginCalculatorComputationActions.setMarginSavedAfterMedicalDentalEffectiveInsurance,
        (state, { savedAfterMedicalDentalEffectiveInsurance }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                savedAfterMedicalDentalEffectiveInsurance
            }
        })
    ),

    on(
        MarginCalculatorApiActions.insuranceTotalCostFixEffectiveDateLoadedSuccess,
        (state, { insuranceTotalCostFixEffectiveDate }): MarginCalculatorState => ({
            ...state,
            insuranceTotalCostFixEffectiveDate
        })
    ),

    on(
        MarginCalculatorPageActions.clinicianNameIsSame,
        (state): MarginCalculatorState => ({
            ...state,
            isClinicianInvalid: false,
            loadClinicianStatus: ApiActionStatus.Success
        })
    ),

    on(
        MarginCalculatorPageActions.loadClinician,
        MarginCalculatorPageActions.loadClinicianByName,
        (state: MarginCalculatorState): MarginCalculatorState => ({
            ...state,
            loadClinicianStatus: ApiActionStatus.InProgress
        })
    ),

    on(
        MarginCalculatorApiActions.clinicianLoadedSuccess,
        MarginCalculatorApiActions.clinicianLoadedByNameSuccess,
        (state: MarginCalculatorState): MarginCalculatorState => ({
            ...state,
            loadClinicianStatus: ApiActionStatus.Success
        })
    ),

    on(
        MarginCalculatorApiActions.clinicianLoadedFail,
        MarginCalculatorApiActions.findClinicianByNameFail,
        (state: MarginCalculatorState): MarginCalculatorState => ({
            ...state,
            loadClinicianStatus: ApiActionStatus.Failed
        })
    ),

    on(
        MarginCalculatorApiActions.findClinicianByNameFail,
        (state): MarginCalculatorState => ({
            ...state,
            isClinicianInvalid: true
        })
    ),

    on(
        MarginCalculatorPageActions.marginCalculatorComponentLoadedV2,
        (state, { marginTypeId }): MarginCalculatorState => ({
            ...state,
            margin: {
                ...state.margin,
                marginTypeId: state.margin?.id ? state.margin.marginTypeId : marginTypeId
            }
        })
    ),

    on(
        MarginCalculatorPageActions.setMarginLinkedToJob,
        UpdateMarginActions.linkSampleMarginToJobSuccess,
        (state, { linkedToJob }): MarginCalculatorState => ({
            ...state,
            linkedToJob
        })
    )
);
